import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import Notification from "../../components/Notification";
import PrayerText from "../../components/PrayerText";
import { apiCall, formatDay } from "../../utils/apiCall";

const PAGE_SIZES = [10, 20, 50, 100];

// Every prayer item sent in with mission feedback, newest mission first.
// Filter by school, term and the mission's date. Open to every member.
function PrayerItems() {
    const navigate = useNavigate();
    const location = useLocation();
    const [list, setList] = useState(null);
    const [schools, setSchools] = useState([]);
    const [terms, setTerms] = useState([]);
    const [school, setSchool] = useState(null);
    const [termId, setTermId] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [searchText, setSearchText] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "Prayer Items - CFCM Portal";
        apiCall("/schools").then(({ ok, data }) => ok && Array.isArray(data) && setSchools(data)).catch(() => {});
        apiCall("/terms").then(({ ok, data }) => ok && setTerms(Array.isArray(data) ? data : data?.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => { setSearch(searchText.trim()); setPage(1); }, 350);
        return () => clearTimeout(timer);
    }, [searchText]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({ page: String(page), per_page: String(perPage) });
            if (school) query.set("school_id", school.value);
            if (termId) query.set("term_id", termId);
            if (from) query.set("from", from);
            if (to) query.set("to", to);
            if (search) query.set("search", search);
            const { ok, status, data } = await apiCall(`/missions/prayer-items?${query}`);
            if (status === 401) return navigate("/logout", { state: { from: location } });
            if (!ok) throw new Error(data?.message);
            setList(data);
            setError("");
        } catch (problem) {
            setError(problem.message || "The prayer items could not be loaded.");
        } finally {
            setLoading(false);
        }
    }, [page, perPage, school, termId, from, to, search, navigate, location]);

    useEffect(() => { load(); }, [load]);

    const choose = (setter) => (value) => { setter(value); setPage(1); };
    const filtered = school || termId || from || to || search;
    const clear = () => { setSchool(null); setTermId(""); setFrom(""); setTo(""); setSearchText(""); setSearch(""); setPage(1); };
    const items = list?.data || [];

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <div>
                            <p className="title is-4">Prayer Items</p>
                            <p className="cf-muted">What schools asked us to pray for, from the feedback sent in after each mission.</p>
                        </div>
                    </div>
                </div>
            </nav>

            {error && <Notification type="danger" message={error} onClose={() => setError("")} />}

            <div className="box">
                <div className="columns is-multiline is-variable is-2">
                    <div className="column is-4-desktop is-6-tablet">
                        <label className="label" htmlFor="prayer-school">School</label>
                        <Select
                            inputId="prayer-school"
                            isClearable
                            options={schools.map((s) => ({ value: s.id, label: s.name }))}
                            value={school}
                            onChange={choose(setSchool)}
                            placeholder="All schools"
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div className="column is-2-desktop is-6-tablet">
                        <label className="label" htmlFor="prayer-term">Term</label>
                        <div className="select is-fullwidth">
                            <select id="prayer-term" value={termId} onChange={(e) => choose(setTermId)(e.target.value)}>
                                <option value="">All terms</option>
                                {terms.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="column is-3-desktop is-6-tablet">
                        <label className="label" htmlFor="prayer-from">Mission date from</label>
                        <input id="prayer-from" type="date" className="input" value={from} max={to || undefined} onChange={(e) => choose(setFrom)(e.target.value)} />
                    </div>
                    <div className="column is-3-desktop is-6-tablet">
                        <label className="label" htmlFor="prayer-to">to</label>
                        <input id="prayer-to" type="date" className="input" value={to} min={from || undefined} onChange={(e) => choose(setTo)(e.target.value)} />
                    </div>
                    <div className="column is-9-desktop is-12-tablet">
                        <label className="label" htmlFor="prayer-search">Search</label>
                        <input id="prayer-search" type="search" className="input" placeholder="Words in the prayer items, or a school" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </div>
                    <div className="column is-3-desktop is-12-tablet cf-prayer-actions">
                        {filtered && <button type="button" className="button is-white" onClick={clear}>Clear filters</button>}
                    </div>
                </div>
            </div>

            {list && (
                <p className="cf-muted mb-3" aria-live="polite">
                    {list.total === 0 ? "No prayer items match." : `${list.total} prayer item${list.total === 1 ? "" : "s"}${filtered ? " match" : ""}.`}
                </p>
            )}

            {!list && loading && <p>Loading the prayer items...</p>}

            <div className="cf-prayer-list" aria-busy={loading}>
                {items.map((item) => {
                    const feedbackLink = `/dashboard/missions/${item.mission_id}/feedback/details`;
                    const converts = Number(item.no_of_converts);
                    return (
                        <article className="box cf-prayer-card mb-0" key={item.id}>
                            <div className="cf-fb-head">
                                <p className="cf-prayer-title">
                                    <Link to={feedbackLink}>{item.school_name || "Unknown School"}</Link>
                                    <span className="cf-prayer-meta">
                                        {" · "}Mission on {formatDay(item.mission_date)}{item.term_name ? ` · ${item.term_name}` : ""}
                                    </span>
                                </p>
                                <Link to={feedbackLink} className="cf-fb-converts" title="Open this mission feedback">
                                    <strong>{item.no_of_converts ?? 0}</strong> {converts === 1 ? "convert" : "converts"}
                                </Link>
                            </div>
                            <PrayerText text={item.prayer_items} previewLength={260} label="" />
                        </article>
                    );
                })}
            </div>

            {list && list.total > 0 && (
                <div className="cf-pager is-split mt-4">
                    <div className="select is-small">
                        <select aria-label="Items per page" value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}>
                            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} per page</option>)}
                        </select>
                    </div>
                    <div className="cf-pager">
                        <button type="button" className="button is-small" disabled={list.current_page <= 1} onClick={() => setPage(list.current_page - 1)}>Previous</button>
                        <span>Page {list.current_page} of {list.last_page}</span>
                        <button type="button" className="button is-small" disabled={list.current_page >= list.last_page} onClick={() => setPage(list.current_page + 1)}>Next</button>
                    </div>
                </div>
            )}
        </main>
    );
}

export default PrayerItems;
